import { apiService } from './api.service'
import {
  Agent,
  AgentDetail,
  AgentExecutionRequest,
  AgentExecutionResponse,
  Conversation,
  ConversationDetail,
  DocumentContext,
} from '../types/agent.types'

/**
 * Agent Service for Office Add-ins API Communication
 *
 * This service provides methods for interacting with AI agents,
 * executing agent skills, and managing conversations within
 * Office Add-in applications.
 */
export class AgentService {
  private readonly basePath = '/v1/agents'
  private readonly conversationsPath = '/v1/conversations'

  /**
   * Get list of active agents
   *
   * @returns Promise resolving to an array of active Agent objects
   *
   * @example
   * ```typescript
   * const agents = await agentService.getAgents();
   * console.log(agents.map(a => a.name));
   * ```
   */
  async getAgents(): Promise<Agent[]> {
    const response = await apiService.get<Agent[]>(`${this.basePath}`)
    return response.data
  }

  /**
   * Get agent details by ID
   *
   * @param agentId - The unique identifier of the agent
   * @returns Promise resolving to the detailed Agent object
   *
   * @example
   * ```typescript
   * const agent = await agentService.getAgent('agent-123');
   * console.log(agent.skills);
   * ```
   */
  async getAgent(agentId: string): Promise<AgentDetail> {
    const response = await apiService.get<AgentDetail>(`${this.basePath}/${agentId}`)
    return response.data
  }

  /**
   * Execute an agent with the given context
   *
   * Sends a prompt to the specified agent along with optional document
   * context from the Office application. The agent processes the request
   * and returns a response with suggested actions.
   *
   * @param agentId - The unique identifier of the agent to execute
   * @param request - The execution request containing prompt and context
   * @returns Promise resolving to the agent's execution response
   *
   * @example
   * ```typescript
   * const response = await agentService.executeAgent('agent-123', {
   *   agentId: 'agent-123',
   *   userMessage: 'Summarize this document',
   *   documentContext: {
   *     documentType: 'word',
   *     selectedText: 'Lorem ipsum...'
   *   },
   *   conversationHistory: []
   * });
   * ```
   */
  async executeAgent(
    agentId: string,
    request: AgentExecutionRequest
  ): Promise<AgentExecutionResponse> {
    const response = await apiService.post<AgentExecutionResponse>(
      `${this.basePath}/${agentId}/execute`,
      request
    )
    return response.data
  }

  /**
   * Execute a specific skill for an agent
   *
   * Skills are specialized capabilities of an agent. This method allows
   * invoking a specific skill directly rather than letting the agent
   * determine which skill to use.
   *
   * @param agentId - The unique identifier of the agent
   * @param skillSlug - The slug identifier of the skill to execute
   * @param request - The execution request containing prompt and context
   * @returns Promise resolving to the skill execution response
   *
   * @example
   * ```typescript
   * const response = await agentService.executeSkill(
   *   'agent-123',
   *   'grammar-check',
   *   {
   *     agentId: 'agent-123',
   *     userMessage: 'Check grammar in selection',
   *     documentContext: { documentType: 'word', selectedText: '...' },
   *     conversationHistory: []
   *   }
   * );
   * ```
   */
  async executeSkill(
    agentId: string,
    skillSlug: string,
    request: AgentExecutionRequest
  ): Promise<AgentExecutionResponse> {
    const response = await apiService.post<AgentExecutionResponse>(
      `${this.basePath}/${agentId}/skills/${skillSlug}/execute`,
      request
    )
    return response.data
  }

  /**
   * Create a new conversation
   *
   * Initializes a new conversation thread with a specific agent.
   * Conversations maintain context across multiple messages.
   *
   * @param agentId - The unique identifier of the agent for this conversation
   * @param title - Optional title for the conversation
   * @returns Promise resolving to the created Conversation object
   *
   * @example
   * ```typescript
   * const conversation = await agentService.createConversation(
   *   'agent-123',
   *   'Financial Report Analysis'
   * );
   * console.log(conversation.id);
   * ```
   */
  async createConversation(agentId: string, title?: string): Promise<Conversation> {
    const response = await apiService.post<Conversation>(this.conversationsPath, {
      agentId,
      title: title || 'New Conversation',
    })
    return response.data
  }

  /**
   * Get conversation history
   *
   * Retrieves the full conversation details including all messages
   * and associated metadata.
   *
   * @param conversationId - The unique identifier of the conversation
   * @returns Promise resolving to the detailed Conversation object with messages
   *
   * @example
   * ```typescript
   * const conversation = await agentService.getConversation('conv-456');
   * conversation.messages.forEach(msg => {
   *   console.log(`${msg.role}: ${msg.content}`);
   * });
   * ```
   */
  async getConversation(conversationId: string): Promise<ConversationDetail> {
    const response = await apiService.get<ConversationDetail>(
      `${this.conversationsPath}/${conversationId}`
    )
    return response.data
  }

  /**
   * Send message to conversation
   *
   * Sends a new message to an existing conversation and receives
   * the agent's response. Optionally includes current document context
   * for context-aware responses.
   *
   * @param conversationId - The unique identifier of the conversation
   * @param message - The message content to send
   * @param documentContext - Optional document context from the Office application
   * @returns Promise resolving to the agent's execution response
   *
   * @example
   * ```typescript
   * const response = await agentService.sendMessage(
   *   'conv-456',
   *   'Can you elaborate on the second point?',
   *   {
   *     documentType: 'word',
   *     documentTitle: 'Q4 Report.docx',
   *     selectedText: 'Revenue growth metrics...'
   *   }
   * );
   * ```
   */
  async sendMessage(
    conversationId: string,
    message: string,
    documentContext?: DocumentContext
  ): Promise<AgentExecutionResponse> {
    const response = await apiService.post<AgentExecutionResponse>(
      `${this.conversationsPath}/${conversationId}/messages`,
      {
        message,
        documentContext,
      }
    )
    return response.data
  }

  /**
   * Get user's recent conversations
   *
   * Retrieves a list of the user's recent conversations, optionally
   * filtered by agent or Office application.
   *
   * @param options - Optional filtering options
   * @returns Promise resolving to an array of Conversation objects
   *
   * @example
   * ```typescript
   * const conversations = await agentService.getConversations({
   *   agentId: 'agent-123',
   *   officeApp: 'word',
   *   limit: 10
   * });
   * ```
   */
  async getConversations(options?: {
    agentId?: string
    officeApp?: 'word' | 'excel' | 'powerpoint' | 'outlook'
    limit?: number
    offset?: number
  }): Promise<Conversation[]> {
    const response = await apiService.get<Conversation[]>(this.conversationsPath, {
      params: options,
    })
    return response.data
  }

  /**
   * Delete a conversation
   *
   * Permanently removes a conversation and all its messages.
   *
   * @param conversationId - The unique identifier of the conversation to delete
   *
   * @example
   * ```typescript
   * await agentService.deleteConversation('conv-456');
   * ```
   */
  async deleteConversation(conversationId: string): Promise<void> {
    await apiService.delete(`${this.conversationsPath}/${conversationId}`)
  }

  /**
   * Get agent by slug
   *
   * Retrieves an agent using its URL-friendly slug identifier.
   *
   * @param slug - The slug identifier of the agent
   * @returns Promise resolving to the Agent object
   *
   * @example
   * ```typescript
   * const agent = await agentService.getAgentBySlug('financial-analyst');
   * ```
   */
  async getAgentBySlug(slug: string): Promise<AgentDetail> {
    const response = await apiService.get<AgentDetail>(`${this.basePath}/slug/${slug}`)
    return response.data
  }

  /**
   * Get available skills for an agent
   *
   * Retrieves the list of skills available for a specific agent.
   *
   * @param agentId - The unique identifier of the agent
   * @returns Promise resolving to an array of skill objects
   *
   * @example
   * ```typescript
   * const skills = await agentService.getAgentSkills('agent-123');
   * skills.forEach(skill => console.log(skill.name));
   * ```
   */
  async getAgentSkills(
    agentId: string
  ): Promise<Array<{ id: string; slug: string; name: string; description: string }>> {
    const response = await apiService.get<
      Array<{ id: string; slug: string; name: string; description: string }>
    >(`${this.basePath}/${agentId}/skills`)
    return response.data
  }
}

export const agentService = new AgentService()
